import React from 'react';
import { linearScale } from './scale';
import { axisTickIndices } from '../../../../lib/format';

const STEP = 25;

// Drawing bounds in user units. Type stays at the same font size; only the
// drawing gets smaller. `default` is the original 560×200 chart.
const CHART_SIZES = {
  default: { width: 560, height: 200, left: 60, right: 540, top: 20, bottom: 170, xLabels: 6 },
  compact: { width: 560, height: 140, left: 60, right: 540, top: 20, bottom: 110, xLabels: 6 },
  small: { width: 300, height: 130, left: 60, right: 272, top: 18, bottom: 100, xLabels: 3 },
};

/**
 * The value axis, in steps of 25 between 0 and 100, covering only the range
 * the data reaches: a line from 52% to 74% gets 50% and 75%, not 0-100.
 */
export function valueDomain(values) {
  let lo = Math.max(0, Math.floor(Math.min(...values) / STEP) * STEP);
  let hi = Math.min(100, Math.ceil(Math.max(...values) / STEP) * STEP);
  if (hi === lo) {
    if (lo === 100) lo -= STEP;
    else hi += STEP;
  }
  const ticks = [];
  for (let t = lo; t <= hi; t += STEP) ticks.push(t);
  return { lo, hi, ticks };
}

/** Consecutive plotted points. A null coordinate ends the run, so the line breaks. */
function runs(coords) {
  const out = [];
  let current = [];
  coords.forEach((point) => {
    if (point == null) {
      if (current.length) out.push(current);
      current = [];
    } else current.push(point);
  });
  if (current.length) out.push(current);
  return out;
}

function areaPath(run, bottom) {
  if (run.length < 2) return null;
  return `M${run[0].join(',')} ${run.slice(1).map((c) => `L${c.join(',')}`).join(' ')} L${run[run.length - 1][0]},${bottom} L${run[0][0]},${bottom} Z`;
}

/**
 * A percentage line over evenly spaced points, from the one scale function.
 * `points` is [{ key, label, value }] in order. A null value keeps its slot
 * and breaks the line. `markers` are extra hollow points ({ index, value })
 * drawn over it. The text alternative is `ariaLabel`.
 */
const LineChart = ({
  points, markers = [], ariaLabel, format = (v) => `${v}%`, size = 'default',
}) => {
  const box = CHART_SIZES[size] || CHART_SIZES.default;
  const { width, height, left, right, top, bottom, xLabels } = box;
  const values = points.map((p) => p.value);
  const numbers = values.filter((value) => value != null);
  const markerValues = markers.map((m) => m.value).filter((value) => value != null);
  const { lo, hi, ticks } = valueDomain([...(numbers.length ? numbers : [0]), ...markerValues]);
  const x = linearScale([0, Math.max(points.length - 1, 0)], [left, right]);
  const y = linearScale([lo, hi], [bottom, top]);
  const coords = points.map((p, i) => (p.value == null ? null : [x(i), y(p.value)]));
  const segments = runs(coords);
  let lastIndex = -1;
  coords.forEach((point, index) => {
    if (point != null) lastIndex = index;
  });
  const last = lastIndex >= 0 ? coords[lastIndex] : null;
  const labelled = new Set(axisTickIndices(points.length, xLabels));
  const chartClass = size === 'compact' || size === 'small' ? `td-act-chart td-act-chart--${size}` : 'td-act-chart';

  return (
    <svg className={chartClass} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      {ticks.map((t) => (
        <g key={t}>
          <line className="td-act-chart__grid" x1={left - 20} y1={y(t)} x2={width - 10} y2={y(t)} />
          <text className="td-act-chart__ytick" x={left - 28} y={y(t) + 4} textAnchor="end">{format(t)}</text>
        </g>
      ))}
      {segments.map((run) => {
        const area = areaPath(run, bottom);
        return area ? <path key={`a-${run[0].join(',')}`} className="td-act-chart__area" d={area} /> : null;
      })}
      {segments.filter((run) => run.length > 1).map((run) => (
        <polyline key={run[0].join(',')} className="td-act-chart__line" points={run.map((c) => c.join(',')).join(' ')} />
      ))}
      {coords.map((coord, i) => (coord ? (
        <circle key={points[i].key} className="td-act-chart__point" cx={coord[0]} cy={coord[1]} r={i === lastIndex ? 5 : 3.5} />
      ) : null))}
      {markers.map((m) => (
        <circle key={`m-${m.index}-${m.value}`} className="td-act-chart__marker" cx={x(m.index)} cy={y(m.value)} r="5" />
      ))}
      {last ? (
        <text className="td-act-chart__last" x={last[0]} y={last[1] - 12} textAnchor={numbers.length > 1 ? 'end' : 'middle'}>
          {format(values[lastIndex])}
        </text>
      ) : null}
      {points.map((p, i) => (labelled.has(i) ? (
        <text key={p.key} className="td-act-chart__xtick" x={x(i)} y={height - 10} textAnchor="middle">{p.label}</text>
      ) : null))}
    </svg>
  );
};

export default LineChart;
